import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface Addon {
    id: number;
    name: string;
    type: 'citizens' | 'storage';
    unit_value: number;
    price: number;
    is_active: boolean;
}

interface AddonFormData {
    name: string;
    type: 'citizens' | 'storage';
    unit_value: number;
    price: number;
    is_active: boolean;
}

const initialFormData: AddonFormData = {
    name: '',
    type: 'citizens',
    unit_value: 1000,
    price: 0,
    is_active: true,
};

const Addons = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [open, setOpen] = useState(false);
    const [selectedAddon, setSelectedAddon] = useState<Addon | null>(null);
    const [formData, setFormData] = useState<AddonFormData>(initialFormData);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addonToDelete, setAddonToDelete] = useState<Addon | null>(null);

    // Fetch addons
    const { data, isLoading } = useQuery({
        queryKey: ['addons', pageModel.page, pageModel.pageSize, typeFilter],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (typeFilter) params.type = typeFilter;
            const response = await api.get('/superadmin/addons', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data: AddonFormData) => {
            if (selectedAddon) {
                return api.put(`/superadmin/addons/${selectedAddon.id}`, data);
            }
            return api.post('/superadmin/addons', data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['addons'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to save addon');
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/addons/${id}`),
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['addons'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to delete addon');
        },
    });

    const handleOpen = (addon?: Addon) => {
        if (addon) {
            setSelectedAddon(addon);
            setFormData({
                name: addon.name,
                type: addon.type,
                unit_value: addon.unit_value,
                price: addon.price,
                is_active: addon.is_active,
            });
        } else {
            setSelectedAddon(null);
            setFormData(initialFormData);
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedAddon(null);
        setFormData(initialFormData);
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (addon: Addon) => {
        setAddonToDelete(addon);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (addonToDelete) {
            deleteMutation.mutate(addonToDelete.id);
        }
        setDeleteDialogOpen(false);
        setAddonToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAddonToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        {
            field: 'type',
            headerName: 'Type',
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={params.value === 'citizens' ? 'primary' : 'secondary'}
                />
            ),
        },
        {
            field: 'unit_value',
            headerName: 'Value',
            width: 100,
            valueFormatter: (value: number, row: any) =>
                row.type === 'citizens' ? `${value.toLocaleString()} users` : `${value} MB`,
        },
        { field: 'price', headerName: 'Price (₹)', width: 100 },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 90,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.value ? 'success' : 'error'}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        {
            field: 'type',
            headerName: 'Type',
            width: 80,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" color={params.value === 'citizens' ? 'primary' : 'secondary'} />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const totalRows = data?.data?.total || data?.total || 0;

    return (
        <Box>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="h3">Addons</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={typeFilter}
                                    label="Type"
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="citizens">Citizens</MenuItem>
                                    <MenuItem value="storage">Storage</MenuItem>
                                </Select>
                            </FormControl>
                            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                                {isMobile ? 'Add' : 'Add Addon'}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>


            {error && !open && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                    <DataGrid
                        rows={rows}
                        columns={isMobile ? mobileColumns : columns}
                        loading={isLoading}
                        rowCount={totalRows}
                        paginationModel={pageModel}
                        onPaginationModelChange={setPageModel}
                        paginationMode="server"
                        pageSizeOptions={[10, 25, 50]}
                        disableRowSelectionOnClick
                        autoHeight
                        sx={{ border: 0 }}
                    />
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedAddon ? 'Edit Addon' : 'Create Addon'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <TextField
                            label="Addon Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={formData.type}
                                label="Type"
                                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'citizens' | 'storage' })}
                            >
                                <MenuItem value="citizens">Citizens (Extra Users)</MenuItem>
                                <MenuItem value="storage">Storage (Extra MB)</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <TextField
                                label={formData.type === 'citizens' ? 'Number of Citizens' : 'Storage (MB)'}
                                type="number"
                                fullWidth
                                required
                                value={formData.unit_value}
                                onChange={(e) => setFormData({ ...formData, unit_value: Number(e.target.value) })}
                            />
                            <TextField
                                label="Price (₹)"
                                type="number"
                                fullWidth
                                required
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{addonToDelete?.name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Addons;
